import { confirmUnsavedChanges } from '@/shared/lib/confirmUnsavedChanges';
import { useUnsavedForm } from '@/shared/hooks/useUnsavedForm';
import { useEffect, useMemo, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, FormProvider } from 'react-hook-form';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { ApiOrderType, type ApiOrderTypeSimpleModel, type ExecutorResponse } from '@/core/generated/api';
import { Link } from '@/shared/ui/Link/Link';
import { ExecutorSearchField, type ExecutorSearchOption } from '@/entities/Executor/ui/ExecutorSearchField';
import { useExecutorAddresses } from '@/entities/ExecutorAddress/model/store';
import { useOrders } from '@/entities/Order/model/store';
import {
    ASSIGN_EXECUTOR_FORM_DEFAULTS,
    createAssignExecutorSchema,
    type AssignExecutorFormValues,
} from '@/entities/Order/model/assignExecutorSchema';
import { RequestStatus } from '@/shared/model/requestStatus';
import { AddressListField } from '@/shared/ui/AddressListField/AddressListField';
import { toAddressOption } from '@/shared/ui/AddressListField/addressOption';
import { Modal } from '@/shared/ui/Modal/Modal';
import { RequestError } from '@/shared/ui/RequestState';
import { createRHFFields } from '@/shared/ui/RHFFields';
import { ExecutorOptionRow } from './ExecutorOptionRow';
import { useAssignExecutorSubmit } from './useAssignExecutorSubmit';
import classes from './AssignExecutorModal.module.scss';

export interface AssignExecutorModalProps {
    visible: boolean;
    onHide: () => void;
    orderNumber: string;
    orderId: string;
    orderType: ApiOrderTypeSimpleModel;
}

const { TextField: CommentField } = createRHFFields<AssignExecutorFormValues>();

/** Предоставляет форму назначения исполнителя на заказ. */
export const AssignExecutorModal = (
    {
        orderId,
        visible,
        onHide,
        orderNumber,
        orderType,
    }: AssignExecutorModalProps
) => {
    const requiresAddress = orderType.id === ApiOrderType.PetSitting
        || orderType.id === ApiOrderType.PetBoarding
        || orderType.id === ApiOrderType.ToSpecialist;

    const schema = useMemo(() => createAssignExecutorSchema(requiresAddress), [requiresAddress]);

    const form = useUnsavedForm<AssignExecutorFormValues>({
        resolver: zodResolver(schema),
        defaultValues: ASSIGN_EXECUTOR_FORM_DEFAULTS,
    }, visible);

    const [selectedExecutor, setSelectedExecutor] = useState<ExecutorResponse | null>(null);
    const { onSubmit, completed, agreementSigningUri, resetCompleted } = useAssignExecutorSubmit(orderId, form);
    const executorAddresses = useExecutorAddresses((state) => state.executorAddresses);
    const listStatus = useExecutorAddresses((state) => state.listStatus);
    const listError = useExecutorAddresses((state) => state.listError);
    const listExecutorId = useExecutorAddresses((state) => state.listExecutorId);
    const getAllExecutorAddresses = useExecutorAddresses((state) => state.getAllExecutorAddresses);
    const clearAddresses = useExecutorAddresses((state) => state.clearAddresses);
    const getOrderById = useOrders((state) => state.getOrderById);
    const { reset } = form;

    useEffect(() => {
        if (!visible) return;
        reset(ASSIGN_EXECUTOR_FORM_DEFAULTS);
        setSelectedExecutor(null);
        resetCompleted();
        clearAddresses();
    }, [visible, orderId, requiresAddress, reset, resetCompleted, clearAddresses]);

    /** Закрывает окно и обновляет заказ после успешного назначения. */
    const handleHide = () => confirmUnsavedChanges(form.formState.isDirty, () => {
        reset();

        if (completed) void getOrderById(orderId);
        onHide();
    });

    /** Выбирает исполнителя и загружает его адреса при необходимости. */
    const handleExecutorChange = (executor: ExecutorResponse | null) => {
        setSelectedExecutor(executor);

        form.setValue('executorId', executor?.id ?? '', {
            shouldValidate: true,
            shouldDirty: true
        });

        form.setValue('executorAddressId', null, {
            shouldValidate: true,
            shouldDirty: true
        });

        clearAddresses();

        if (requiresAddress && executor?.id) void getAllExecutorAddresses(executor.id);
    };

    const selectedExecutorId = form.watch('executorId');

    const addressesForExecutor = listExecutorId === selectedExecutorId
        ? executorAddresses
        : [];

    const addressOptions = addressesForExecutor
        .map((item) => toAddressOption(item.address, item.address?.id, item.isFavorite))
        .filter((option) => option !== null);

    return <Modal
        visible={visible}
        onHide={handleHide}
        title={`Назначить исполнителя на заказ №${orderNumber}`}
        size="lg"
        className={classes.modal}
        closable={!form.formState.isSubmitting}
        footer={<div className={classes.footer}>
            <Button
                type="button"
                label={completed
                    ? 'Закрыть'
                    : 'Отмена'
                }
                text
                onClick={handleHide}
                disabled={form.formState.isSubmitting}
            />
            {!completed &&
                <Button
                    type="submit"
                    form="assign-executor-form"
                    label="Назначить"
                    icon="pi pi-user-plus"
                    loading={form.formState.isSubmitting}
                    disabled={form.formState.isSubmitting}
                />
            }
        </div>}
    >
        {completed
            ? <div className={classes.result}>
                <Message
                    severity="success"
                    text="Исполнитель назначен на заказ"
                />
                {agreementSigningUri &&
                    <div className={classes.agreement}>
                        <span>Договор к заказу для исполнителя:</span>
                        <Link
                            isBlank
                            url={agreementSigningUri}
                            text={`Договор к заказу ${orderNumber}`}
                            showCopyButton
                        />
                        <span>Передайте ссылку исполнителю</span>
                    </div>
                }
            </div>
            : <FormProvider {...form}>
                <form
                    id="assign-executor-form"
                    onSubmit={form.handleSubmit(onSubmit)}
                    className={classes.form}
                    noValidate
                >
                    <Controller
                        name="executorId"
                        control={form.control}
                        render={({ fieldState }) => <div className={classes.formSection}>
                            <ExecutorSearchField
                                label="Исполнитель"
                                name="executorId"
                                value={selectedExecutor}
                                onChange={handleExecutorChange}
                                itemTemplate={(option: ExecutorSearchOption) => option.data
                                    ? <ExecutorOptionRow
                                        executor={option.data}
                                        selected={option.id === selectedExecutorId}
                                    />
                                    : option.name}
                                panelClassName={classes.resultsPanel}
                                scrollHeight="24rem"
                                error={fieldState.error?.message}
                                disabled={form.formState.isSubmitting}
                                isRequired
                            />
                            {selectedExecutor?.id === selectedExecutorId &&
                                <div className={classes.selectedCard}>
                                    <span className={classes.selectedLabel}>Выбранный исполнитель</span>
                                    <ExecutorOptionRow
                                        executor={selectedExecutor}
                                        selected
                                    />
                                </div>
                            }
                        </div>}
                    />

                    {requiresAddress &&
                        <Controller
                            name="executorAddressId"
                            control={form.control}
                            render={({ field, fieldState }) => <div className={classes.formSection}>
                                <AddressListField
                                    name={field.name}
                                    label="Адрес исполнителя"
                                    ownerId={selectedExecutorId}
                                    emptyOwnerMessage="Выберите исполнителя — здесь появятся его адреса"
                                    emptyListMessage="У исполнителя пока нет адресов"
                                    helpText="Выберите адрес проведения услуги"
                                    value={field.value ?? ''}
                                    onChange={(value) => form.setValue('executorAddressId', value || null, {
                                        shouldValidate: true,
                                        shouldDirty: true,
                                        shouldTouch: true,
                                    })}
                                    onBlur={field.onBlur}
                                    options={addressOptions}
                                    status={listExecutorId === selectedExecutorId
                                        ? listStatus
                                        : RequestStatus.Idle
                                    }
                                    requestError={listError}
                                    onRetry={() => void getAllExecutorAddresses(selectedExecutorId)}
                                    disabled={!selectedExecutorId || form.formState.isSubmitting}
                                    error={fieldState.error?.message}
                                    isRequired
                                />
                            </div>}
                        />
                    }

                    <CommentField
                        name="comment"
                        label="Комментарий"
                        disabled={form.formState.isSubmitting}
                    />
                    {form.formState.errors.root?.server?.message &&
                        <RequestError
                            message={form.formState.errors.root.server.message}
                            compact
                        />
                    }
                </form>
            </FormProvider>}
    </Modal>;
};
